import { FormField } from '../types/form';
import { isMultiRowOptions } from '../types/guards';

/**
 * Is a single field "complete enough" to consider the form done?
 *
 * Mirrors the per-type rules in PublicForm/AnonymousFormFillForm validateForm so
 * the scheduler gate and submit-validation never disagree:
 *  - header / logic / image: presentational, never block.
 *  - scheduler: skipped here (it's the thing being gated).
 *  - av_check / connection_check: complete only when status === 'pass'.
 *  - multi_row: needs at least one row, and every required column filled.
 *  - checkbox_with_input: needs a checked option with a non-empty input.
 *  - everything else: complete when it has a truthy value.
 *
 * Optional (non-required) plain fields are always "complete"; required-ness is
 * still enforced for the interactive checks, which must pass regardless.
 */
export const isFieldComplete = (
  field: FormField,
  formData: { [key: string]: any },
): boolean => {
  const value = formData?.[field.id];

  switch (field.field_type) {
    case 'header':
    case 'logic':
    case 'image':
    case 'scheduler':
      return true;

    case 'av_check':
    case 'connection_check':
      // The readiness checks must actively pass before scheduling unlocks.
      return value?.status === 'pass';

    case 'multi_row': {
      if (!field.is_required) return true;
      const columns =
        field.options && isMultiRowOptions(field.options) ? field.options.columns || [] : [];
      const rows: any[][] = Array.isArray(value) ? value : [];
      if (rows.length === 0) return false;
      return rows.every((row) =>
        columns.every((col, i) => !col.required || (row[i] !== undefined && row[i] !== '')),
      );
    }

    case 'checkbox_with_input': {
      if (!field.is_required) return true;
      if (!value || typeof value !== 'object') return false;
      const options = Object.values(value) as Array<{ checked?: boolean; value?: string }>;
      const checked = options.filter((o) => o?.checked);
      return checked.length > 0 && checked.every((o) => (o.value ?? '').trim() !== '');
    }

    case 'checkbox': {
      if (!field.is_required) return true;
      // Multi-select checkbox stores an array (or an object of booleans); need ≥1.
      if (Array.isArray(value)) return value.length > 0;
      if (value && typeof value === 'object') return Object.values(value).some(Boolean);
      return !!value;
    }

    default:
      if (!field.is_required) return true;
      return value !== undefined && value !== null && String(value).trim() !== '';
  }
};

/**
 * Whether a 'scheduler' field should be unlocked.
 *
 * The interview calendar opens only once the ENTIRE form is complete — every
 * field (except other schedulers) must satisfy isFieldComplete. That covers
 * required survey answers AND the av_check / connection_check passing. If the
 * form somehow has no other fields, the scheduler is shown.
 */
export const shouldShowSchedulerField = (
  field: FormField,
  formData: { [key: string]: any },
  allFields: FormField[],
): boolean => {
  return allFields
    .filter((f) => f.id !== field.id && f.field_type !== 'scheduler')
    .every((f) => isFieldComplete(f, formData));
};
